/**
 * Equipment Description Generator
 * Generates "mad libs" style merchant collections for equipment generation
 */

import {
    itemPools,
    descriptors,
    themedCollections,
    accessories,
    sources
} from '@/Data/EquipmentGeneratorData.js';

/**
 * Pick a random element from an array
 */
function pickRandom(array) {
    return array[Math.floor(Math.random() * array.length)];
}

/**
 * Pick multiple unique random elements from an array
 */
function pickMultipleRandom(array, count) {
    const shuffled = [...array].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, array.length));
}

/**
 * Get items from a pool path like "weapons.swords"
 */
function getPoolItems(poolPath, filter = null) {
    const parts = poolPath.split('.');
    let pool = itemPools;

    for (const part of parts) {
        pool = pool[part];
        if (!pool) return [];
    }

    if (Array.isArray(pool)) {
        if (filter && filter.length > 0) {
            const filtered = pool.filter(item => filter.includes(item));
            return filtered.length > 0 ? filtered : pool;
        }
        return pool;
    }

    return [];
}

/**
 * Get the category key for an item (for accessory matching)
 */
function getItemCategory(poolPath) {
    const parts = poolPath.split('.');
    const category = parts[0];
    const subcategory = parts[1];

    if (category === 'weapons') {
        if (['swords', 'axes', 'blunt', 'polearms'].includes(subcategory)) return subcategory === 'swords' ? 'swords' : 'axes';
        if (subcategory === 'daggers') return 'daggers';
        if (subcategory === 'ranged') return 'ranged';
    }
    if (category === 'armor') return 'armor';
    if (category === 'consumables' && subcategory === 'potions') return 'potions';
    if (category === 'magic' && subcategory === 'books') return 'books';
    if (category === 'tools') return 'tools';
    if (category === 'jewelry') return 'jewelry';
    if (category === 'clothing') return 'clothing';

    return null;
}

/**
 * Apply random descriptors to an item
 */
function applyDescriptors(itemName) {
    const descriptorTypes = Object.keys(descriptors);
    const selectedTypes = pickMultipleRandom(descriptorTypes, Math.random() < 0.6 ? 2 : 1);

    const appliedDescriptors = selectedTypes.map(type => pickRandom(descriptors[type]));

    return {
        name: itemName,
        descriptors: appliedDescriptors
    };
}

/**
 * Maybe add an accessory to an item (30% chance)
 */
function maybeAddAccessory(item, poolPath) {
    if (Math.random() > 0.3) return item;

    const category = getItemCategory(poolPath);
    if (category && accessories[category]) {
        item.accessory = pickRandom(accessories[category]);
    }

    return item;
}

/**
 * Generate items from a themed collection
 */
function generateFromTheme(theme, count) {
    const items = [];
    const themeItems = theme.items.slice(0, count);

    for (const itemDef of themeItems) {
        const poolItems = getPoolItems(itemDef.pool, itemDef.filter);
        if (poolItems.length > 0) {
            const itemName = pickRandom(poolItems);
            let item = applyDescriptors(itemName);
            item = maybeAddAccessory(item, itemDef.pool);
            item.poolPath = itemDef.pool;
            items.push(item);
        }
    }

    return { items, source: theme.source, themeName: theme.name };
}

/**
 * Generate a random mix of items (not themed)
 */
function generateRandomMix(count) {
    const items = [];
    const categories = Object.keys(itemPools);

    for (let i = 0; i < count; i++) {
        const category = pickRandom(categories);
        const subcategories = Object.keys(itemPools[category]);
        const subcategory = pickRandom(subcategories);
        const poolPath = `${category}.${subcategory}`;
        const poolItems = getPoolItems(poolPath);

        if (poolItems.length > 0) {
            const itemName = pickRandom(poolItems);
            let item = applyDescriptors(itemName);
            item = maybeAddAccessory(item, poolPath);
            item.poolPath = poolPath;
            items.push(item);
        }
    }

    return { items, source: pickRandom(sources), themeName: null };
}

/**
 * Format a single item with its descriptors
 */
function formatItem(item) {
    const descStr = item.descriptors.join(' ');
    let result = `${descStr} ${item.name}`;

    if (item.accessory) {
        result += ` with ${item.accessory}`;
    }

    return result;
}

/**
 * Format the collection into a natural language description
 */
function formatAsDescription(generatedData) {
    const { items, source, themeName } = generatedData;
    const count = items.length;

    if (count === 1) {
        const item = items[0];
        const formattedItem = formatItem(item);
        const templates = [
            `A ${formattedItem}`,
            `One ${formattedItem}`,
            `${capitalize(formattedItem)}`
        ];
        return pickRandom(templates);
    }

    if (count === 2) {
        const item1 = formatItem(items[0]);
        const item2 = formatItem(items[1]);
        const templates = [
            `A ${item1} paired with a ${item2}`,
            `${capitalize(item1)} and a ${item2}`,
            `A set: ${item1} alongside ${item2}`
        ];
        return pickRandom(templates);
    }

    // 3-4 items - use collection format
    const formattedItems = items.map(formatItem);
    const lastItem = formattedItems.pop();
    const itemList = formattedItems.join(', ') + ', and ' + lastItem;

    const templates = [
        `From a ${source}: ${itemList}`,
        `A ${source}'s stock: ${itemList}`,
        `${capitalize(source)} goods including ${itemList}`,
        `Collection from a ${source}: ${itemList}`
    ];

    return pickRandom(templates);
}

/**
 * Capitalize the first letter of a string
 */
function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Main entry point - generates a merchant collection
 * @returns {{ description: string, quantity: number }}
 */
export function generateMerchantCollection() {
    // Equal distribution for quantity (1-4)
    const count = Math.floor(Math.random() * 4) + 1;

    // 70% themed, 30% random mix
    const useTheme = Math.random() < 0.7;

    let generatedData;

    if (useTheme) {
        const theme = pickRandom(themedCollections);
        generatedData = generateFromTheme(theme, count);
    } else {
        generatedData = generateRandomMix(count);
    }

    // If we didn't get enough items, fill with random
    while (generatedData.items.length < count) {
        const randomData = generateRandomMix(1);
        if (randomData.items.length > 0) {
            generatedData.items.push(randomData.items[0]);
        } else {
            break;
        }
    }

    const description = formatAsDescription(generatedData);

    return {
        description,
        quantity: generatedData.items.length
    };
}

export default { generateMerchantCollection };
